import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  TextField,
  Button,
  Pagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Psychology as PsychologyIcon, 
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  Launch as LaunchIcon,
  Summarize as SummarizeIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { solarizedlight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getRedditPosts, updateRedditPostScore, batchScoreRedditPosts, submitBatchScoringResults, resetAllRedditPostScores, renderLLMPrompt, customFetch } from '../utils/api';
import { ScoringResult, initializeOpenRouterConfig } from '../utils/openrouter';

interface Post {
  url_md5: string;
  title: string;
  url: string;
  created_time: string;
  inserted_time: string;
  json_data: string;
  score?: number;
}

interface LLMModel {
  id: string;
  name: string;
  description: string;
  provider: string;
  temperature: number;
  max_tokens: number;
  sort_order: number;
  is_default: boolean;
  input_price: number;
  output_price: number;
  pricing_info?: string;
}

const SavedPostsList = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPost, setSelectedPost] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lowScore, setLowScore] = useState(5);
  const [highScore, setHighScore] = useState(10);
  const [scoringModalOpen, setScoringModalOpen] = useState(false);
  const [currentScoringPost, setCurrentScoringPost] = useState<Post | null>(null);
  const [completePrompt, setCompletePrompt] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isRunningLLM, setIsRunningLLM] = useState(false);
  const [scoringResult, setScoringResult] = useState<ScoringResult | null>(null);
  const [selectedModel, setSelectedModel] = useState('');
  
  // Dynamic model loading states
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Summary modal states
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [currentSummaryPost, setCurrentSummaryPost] = useState<Post | null>(null);
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [summaryActiveTab, setSummaryActiveTab] = useState(0);
  const [isRunningSummaryLLM, setIsRunningSummaryLLM] = useState(false);
  const [summaryResult, setSummaryResult] = useState<ScoringResult | null>(null);
  const [summarySelectedModel, setSummarySelectedModel] = useState('');

  // Batch scoring states
  const [batchPostCount, setBatchPostCount] = useState(10);
  const [batchRounds, setBatchRounds] = useState(1);
  const [batchScoringInProgress, setBatchScoringInProgress] = useState(false);
  const [batchScoringResults, setBatchScoringResults] = useState<any[]>([]);
  const [showBatchResults, setShowBatchResults] = useState(false);
  const [batchScoringSelectedModel, setBatchScoringSelectedModel] = useState('');
  
  // Multi-round batch scoring state
  const [multiRoundInProgress, setMultiRoundInProgress] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [multiRoundResults, setMultiRoundResults] = useState<{
    totalScored: number;
    scoreDistribution: { [key: number]: number };
    roundResults: Array<{ round: number; scored: number; }>;
    startTime?: Date;
    endTime?: Date;
    totalTimeElapsed?: string;
  }>({
    totalScored: 0,
    scoreDistribution: {},
    roundResults: []
  });
  
  // Batch prompt review states
  const [promptReviewOpen, setPromptReviewOpen] = useState(false);
  const [batchPromptToReview, setBatchPromptToReview] = useState('');
  const [currentBatchData, setCurrentBatchData] = useState<any>(null);

  // Reset scores states
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetInProgress, setResetInProgress] = useState(false);

  // Helper function to convert datetime-local format to API format
  const formatDateForAPI = useCallback((dateTimeLocal: string) => {
    if (!dateTimeLocal) return '';
    // datetime-local format: YYYY-MM-DDTHH:mm
    // Convert to date only for API: YYYY-MM-DD
    return dateTimeLocal.split('T')[0];
  }, []);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const postsPerPage = 20;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        search: searchTerm,
        created_time_after: formatDateForAPI(startDate),
        created_time_before: formatDateForAPI(endDate),
        score__gte: lowScore > 0 ? lowScore : undefined,
        score__lte: highScore < 10 ? highScore : undefined,
      };
      const response = await getRedditPosts(params);
      if (!response.ok) {
        throw new Error('Failed to fetch posts.');
      }
      const data = await response.json();
      setPosts(data.results);
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / postsPerPage));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, startDate, endDate, lowScore, highScore, formatDateForAPI]);

  useEffect(() => {
    fetchPosts();
    // Initialize OpenRouter configuration from backend (kept for backward compatibility)
    initializeOpenRouterConfig();
  }, [fetchPosts]);

  const handlePostClick = (postId: string) => {
    setSelectedPost(selectedPost === postId ? null : postId);
  };

  const handleFilter = () => {
    setPage(1);
    fetchPosts();
  };
  
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Generate complete scoring prompt using database template
  const generateScoringPrompt = async (post: Post) => {
    const jsonData = JSON.parse(post.json_data);
    
    // Extract comments (limit to top 5-10 most relevant)
    const comments = [];
    if (jsonData.comments && Array.isArray(jsonData.comments)) {
      for (let i = 0; i < Math.min(10, jsonData.comments.length); i++) {
        const comment = jsonData.comments[i];
        if (comment.body && comment.body.length > 50) {
          const truncatedBody = comment.body.length > 500 
            ? comment.body.substring(0, 500) + '...'
            : comment.body;
          comments.push(`• ${truncatedBody}`);
        }
      }
    }
    
    const commentsText = comments.length > 0 ? comments.join('\n') : 'No significant comments';
    
    // Prepare template data
    const templateData = {
      POST_TITLE: jsonData.title || post.title,
      POST_URL: post.url,
      POST_SUBREDDIT: jsonData.subreddit || 'unknown',
      POST_SCORE: jsonData.score || 0,
      POST_CREATED_TIME: new Date(post.created_time).toLocaleString(),
      POST_AUTHOR: jsonData.author || 'unknown',
      POST_CONTENT: jsonData.selftext || jsonData.body || 'No content available',
      POST_COMMENTS: commentsText
    };
    
    try {
      // Fetch and render the prompt template from database
      const response = await renderLLMPrompt('reddit_scoring', templateData);
      if (!response.ok) {
        throw new Error('Failed to fetch prompt template');
      }
      const data = await response.json();
      return data.rendered_content;
    } catch (error) {
      console.error('Error fetching prompt template:', error);
      // Fallback to a basic prompt if database fetch fails
      return `Error loading prompt template. Please check the LLM Prompt configuration in admin panel.`;
    }
  };

  // Generate summary prompt using database template
  const generateSummaryPrompt = async (post: Post) => {
    const jsonData = JSON.parse(post.json_data);
    
    // Extract comments for summary
    const comments = [];
    if (jsonData.comments && Array.isArray(jsonData.comments)) {
      for (const comment of jsonData.comments.slice(0, 10)) { // Top 10 comments
        if (comment.body && comment.body.length > 20) {
          comments.push(`• Author: u/${comment.author || 'unknown'} (${comment.score || 0} upvotes)\n  ${comment.body}`);
        }
      }
    }
    
    const commentsText = comments.length > 0 ? comments.join('\n\n') : 'No significant comments';
    
    // Prepare template data
    const templateData = {
      POST_TITLE: jsonData.title || post.title,
      POST_URL: post.url,
      POST_SUBREDDIT: jsonData.subreddit || 'unknown',
      POST_SCORE: jsonData.score || 0,
      POST_CREATED_TIME: new Date(post.created_time).toLocaleString(),
      POST_AUTHOR: jsonData.author || 'unknown',
      POST_CONTENT: jsonData.selftext || jsonData.body || 'No content available',
      POST_COMMENTS: commentsText
    };
    
    try {
      // Fetch and render the prompt template from database
      const response = await renderLLMPrompt('reddit_summary', templateData);
      if (!response.ok) {
        throw new Error('Failed to fetch summary prompt template');
      }
      const data = await response.json();
      return data.rendered_content;
    } catch (error) {
      console.error('Error fetching summary prompt template:', error);
      // Fallback to a basic prompt if database fetch fails
      return `Error loading summary prompt template. Please check the LLM Prompt configuration in admin panel.`;
    }
  };

  // Handle scoring button click
  const handleScorePost = async (post: Post, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering post expansion
    setCurrentScoringPost(post);
    
    // Show modal with loading state first
    setCompletePrompt('Loading prompt template...');
    setScoringModalOpen(true);
    
    try {
      const prompt = await generateScoringPrompt(post);
      setCompletePrompt(prompt);
    } catch (error) {
      console.error('Error generating scoring prompt:', error);
      setCompletePrompt('Error loading prompt template. Please check the LLM Prompt configuration in admin panel.');
    }
  };

  // Handle summary button click
  const handleSummaryPost = async (post: Post, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering post expansion
    setCurrentSummaryPost(post);
    
    // Show modal with loading state first
    setSummaryPrompt('Loading summary prompt template...');
    setSummaryModalOpen(true);
    
    try {
      const prompt = await generateSummaryPrompt(post);
      setSummaryPrompt(prompt);
    } catch (error) {
      console.error('Error generating summary prompt:', error);
      setSummaryPrompt('Error loading summary prompt template. Please check the LLM Prompt configuration in admin panel.');
    }
  };

  // Close scoring modal
  const handleCloseScoringModal = () => {
    setScoringModalOpen(false);
    setCurrentScoringPost(null);
    setCompletePrompt('');
    setActiveTab(0);
    setScoringResult(null);
    setIsRunningLLM(false);
  };

  // Close summary modal
  const handleCloseSummaryModal = () => {
    setSummaryModalOpen(false);
    setCurrentSummaryPost(null);
    setSummaryPrompt('');
    setSummaryActiveTab(0);
    setSummaryResult(null);
    setIsRunningSummaryLLM(false);
  };

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle summary tab change
  const handleSummaryTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSummaryActiveTab(newValue);
  };

  // Helper function to call LLM directly for batch scoring (bypassing post-specific logic)
  const callLLMForBatchScoring = async (prompt: string, model: string) => {
    const response = await customFetch('/api/llm/call/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: prompt,
        model_id: model
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to call LLM'
      };
    }

    const result = await response.json();
    return {
      success: true,
      content: result.content,
      usage: result.usage || null
    };
  };

  // Fetch available LLM models from backend
  const fetchAvailableModels = useCallback(async () => {
    try {
      setModelsLoading(true);
      const response = await customFetch('/api/llm/available-models/');
      if (response.ok) {
        const data = await response.json();
        setAvailableModels(data.models || []);
        // Set default model if none selected
        if (data.models && data.models.length > 0) {
          if (!selectedModel) {
            setSelectedModel(data.models[0].id);
          }
          if (!summarySelectedModel) {
            setSummarySelectedModel(data.models[0].id);
          }
          if (!batchScoringSelectedModel) {
            setBatchScoringSelectedModel(data.models[0].id);
          }
        }
      } else {
        console.error('Failed to fetch available models');
        setAvailableModels([]);
      }
    } catch (err) {
      console.error('Failed to fetch available models:', err);
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, [selectedModel, summarySelectedModel, batchScoringSelectedModel]);

  // Fetch models on component mount
  useEffect(() => {
    fetchAvailableModels();
  }, [fetchAvailableModels]);

  // Extract numerical score from LLM response text
  const extractScoreFromResponse = (responseText: string): number | null => {
    // Look for patterns like "RELEVANCE SCORE: 5", "Score: 8", "score of 3", etc.
    const scorePatterns = [
      /RELEVANCE\s+SCORE:\s*(\d+)/i,
      /SCORE:\s*(\d+)/i,
      /score\s+(?:of\s+)?(\d+)/i,
      /(\d+)(?:\s*\/\s*10)?(?:\s+out\s+of\s+10)?/i
    ];

    for (const pattern of scorePatterns) {
      const match = responseText.match(pattern);
      if (match && match[1]) {
        const score = parseInt(match[1], 10);
        // Validate score is reasonable (0-100 range)
        if (!isNaN(score) && score >= 0 && score <= 100) {
          return score;
        }
      }
    }

    return null;
  };

  // Run LLM scoring using backend API
  const handleRunLLM = async () => {
    if (!completePrompt || !currentScoringPost || !selectedModel) return;
    
    setIsRunningLLM(true);
    try {
      const response = await customFetch('/api/reddit/post-scoring/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_url_md5: currentScoringPost.url_md5,
          prompt: completePrompt,
          llm_model: selectedModel
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to score post');
      }

      const result = await response.json();
      
      // Convert backend response to ScoringResult format
      const scoringResult: ScoringResult = {
        success: result.success,
        content: result.content,
        usage: result.usage,
        response_time: 0 // Backend doesn't provide this currently
      };

      setScoringResult(scoringResult);
      setActiveTab(1); // Switch to results tab

      // If analysis was successful, extract and save the score
      if (result.success && result.content && currentScoringPost) {
        const extractedScore = extractScoreFromResponse(result.content);
        
        if (extractedScore !== null) {
          try {
            console.log(`💾 Saving score ${extractedScore} for post ${currentScoringPost.url_md5}`);
            const saveResponse = await updateRedditPostScore(currentScoringPost.url_md5, extractedScore);
            
            if (saveResponse.ok) {
              const responseData = await saveResponse.json();
              console.log('✅ Score saved successfully:', responseData);
              
              // Update the result to show the score was saved
              setScoringResult(prev => prev ? {
                ...prev,
                scoreSaved: true,
                savedScore: extractedScore
              } : null);
              
              // Refresh the posts list to show updated score
              fetchPosts();
            } else {
              console.warn('⚠️ Failed to save score:', saveResponse.status);
            }
          } catch (saveError) {
            console.error('❌ Error saving score:', saveError);
          }
        } else {
          console.warn('⚠️ Could not extract numerical score from LLM response');
        }
      }
    } catch (error: any) {
      console.error('Error running LLM:', error);
      setScoringResult({
        success: false,
        error: error.message || 'An unexpected error occurred while running the analysis'
      });
    } finally {
      setIsRunningLLM(false);
    }
  };

  // Run summary LLM using backend API
  const handleRunSummaryLLM = async () => {
    if (!summaryPrompt || !currentSummaryPost || !summarySelectedModel) return;

    setIsRunningSummaryLLM(true);
    try {
      const response = await customFetch('/api/reddit/post-scoring/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_url_md5: currentSummaryPost.url_md5,
          prompt: summaryPrompt,
          llm_model: summarySelectedModel
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate summary');
      }

      const result = await response.json();
      
      // Convert backend response to ScoringResult format
      const summaryResult: ScoringResult = {
        success: result.success,
        content: result.content,
        usage: result.usage,
        response_time: 0 // Backend doesn't provide this currently
      };

      setSummaryResult(summaryResult);
      setSummaryActiveTab(1); // Switch to results tab
    } catch (error: any) {
      console.error('Error running summary LLM:', error);
      setSummaryResult({
        success: false,
        error: error.message || 'An unexpected error occurred while generating the summary'
      });
    } finally {
      setIsRunningSummaryLLM(false);
    }
  };

  // Handle reset all scores
  const handleResetAllScores = async () => {
    setResetInProgress(true);
    setError('');
    
    try {
      const response = await resetAllRedditPostScores();
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset scores');
      }
      
      const data = await response.json();
      if (data.success) {
        // Refresh the posts list to show updated scores
        fetchPosts();
        setResetConfirmOpen(false);
        // Show success message (you can add a success state if needed)
        console.log(`✅ Successfully reset scores for ${data.updated_count} posts`);
      } else {
        throw new Error(data.error || 'Failed to reset scores');
      }
      
    } catch (err: any) {
      setError(`Failed to reset scores: ${err.message}`);
    } finally {
      setResetInProgress(false);
    }
  };

  // Handle batch scoring - Step 1: Get batch data and show prompt review
  const handleBatchScoring = async () => {
    if (batchScoringInProgress || multiRoundInProgress) return;
    
    // If rounds > 1, start multi-round batch scoring
    if (batchRounds > 1) {
      await handleMultiRoundBatchScoring();
      return;
    }
    
    // Single round batch scoring (original behavior)
    setBatchScoringInProgress(true);
    setError('');
    setBatchScoringResults([]);
    
    try {
      // Step 1: Get posts for batch scoring
      const response = await batchScoreRedditPosts(batchPostCount);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to get posts for batch scoring');
      }
      
      const batchData = await response.json();
      
      if (!batchData.success || batchData.posts_count === 0) {
        setError(batchData.message || 'No posts found for scoring');
        setBatchScoringInProgress(false);
        return;
      }
      
      // Step 2: Show prompt review modal instead of immediately calling LLM
      setCurrentBatchData(batchData);
      setBatchPromptToReview(batchData.batch_prompt);
      setPromptReviewOpen(true);
      setBatchScoringInProgress(false); // Reset since we're showing modal first
      
    } catch (err: any) {
      setError(`Failed to prepare batch scoring: ${err.message}`);
      setBatchScoringInProgress(false);
    }
  };
  
  // Handle actual LLM execution after prompt review
  const handleExecuteBatchScoring = async () => {
    setBatchScoringInProgress(true);
    setPromptReviewOpen(false);
    setError('');
    
    try {
      // Step 2: Send batch prompt to LLM (with potentially modified prompt)
      const llmResult = await callLLMForBatchScoring(batchPromptToReview, batchScoringSelectedModel);
      
      if (!llmResult.success || !llmResult.content) {
        throw new Error(llmResult.error || 'Failed to get LLM response');
      }
      
      // Step 3: Submit results back to backend for processing
      const postUrlMd5s = currentBatchData.posts.map((post: any) => post.url_md5);
      const submitResponse = await submitBatchScoringResults(llmResult.content, postUrlMd5s);
      
      if (!submitResponse.ok) {
        const errorData = await submitResponse.json();
        throw new Error(errorData.error || 'Failed to process batch scoring results');
      }
      
      const submitData = await submitResponse.json();
      
      if (submitData.success) {
        setBatchScoringResults(submitData.results);
        setShowBatchResults(true);
        // Refresh the posts list to show updated scores
        fetchPosts();
      } else {
        throw new Error(submitData.error || 'Failed to update post scores');
      }
      
    } catch (err: any) {
      setError(`Batch scoring failed: ${err.message}`);
    } finally {
      setBatchScoringInProgress(false);
    }
  };
  
  // Handle canceling prompt review
  const handleCancelPromptReview = () => {
    setPromptReviewOpen(false);
    setBatchPromptToReview('');
    setCurrentBatchData(null);
  };

  // Handle multi-round batch scoring
  const handleMultiRoundBatchScoring = async () => {
    const startTime = new Date();
    setMultiRoundInProgress(true);
    setCurrentRound(0);
    setTotalRounds(batchRounds);
    setMultiRoundResults({
      totalScored: 0,
      scoreDistribution: {},
      roundResults: [],
      startTime: startTime
    });
    
    const scoreDistribution: { [key: number]: number } = {};
    const roundResults: Array<{ round: number; scored: number; }> = [];
    let totalScored = 0;
    
    try {
      for (let round = 1; round <= batchRounds; round++) {
        setCurrentRound(round);
        
        // Get batch data for this round
        const batchResponse = await batchScoreRedditPosts(batchPostCount);
        if (!batchResponse.ok) {
          const errorData = await batchResponse.json();
          console.warn(`Round ${round} failed: ${errorData.message}`);
          roundResults.push({ round, scored: 0 });
          continue;
        }
        
        const batchData = await batchResponse.json();
        if (!batchData.success || batchData.posts_count === 0) {
          console.warn(`Round ${round}: No posts to score`);
          roundResults.push({ round, scored: 0 });
          continue;
        }
        
        // Execute LLM scoring directly (no prompt review for multi-round)
        const llmResponse = await callLLMForBatchScoring(batchData.batch_prompt, batchScoringSelectedModel);
        if (!llmResponse.success) {
          console.error(`Round ${round} LLM error:`, llmResponse.error);
          roundResults.push({ round, scored: 0 });
          continue;
        }
        
        // Submit results
        const postUrlMd5s = batchData.posts.map((p: any) => p.url_md5);
        const submitResponse = await submitBatchScoringResults(llmResponse.content || '', postUrlMd5s);
        if (!submitResponse.ok) {
          console.error(`Round ${round} submit error`);
          roundResults.push({ round, scored: 0 });
          continue;
        }
        
        const submitData = await submitResponse.json();
        const roundScored = submitData.results?.length || 0;
        totalScored += roundScored;
        roundResults.push({ round, scored: roundScored });
        
        // Update score distribution
        if (submitData.results) {
          submitData.results.forEach((result: any) => {
            const score = result.score;
            scoreDistribution[score] = (scoreDistribution[score] || 0) + 1;
          });
        }
        
        // Update progress
        setMultiRoundResults(prev => ({
          ...prev,
          totalScored,
          scoreDistribution: { ...scoreDistribution },
          roundResults: [...roundResults]
        }));
        
        // Small delay between rounds to prevent overwhelming the API
        if (round < batchRounds) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Calculate elapsed time and finalize results
      const endTime = new Date();
      const totalTimeMs = endTime.getTime() - startTime.getTime();
      const hours = Math.floor(totalTimeMs / (1000 * 60 * 60));
      const minutes = Math.floor((totalTimeMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((totalTimeMs % (1000 * 60)) / 1000);
      
      let timeElapsedStr = '';
      if (hours > 0) {
        timeElapsedStr = `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        timeElapsedStr = `${minutes}m ${seconds}s`;
      } else {
        timeElapsedStr = `${seconds}s`;
      }
      
      // Final results update with timing
      setMultiRoundResults({
        totalScored,
        scoreDistribution: { ...scoreDistribution },
        roundResults: [...roundResults],
        startTime: startTime,
        endTime: endTime,
        totalTimeElapsed: timeElapsedStr
      });
      
      // Refresh posts after all rounds complete
      await fetchPosts();
      
    } catch (error) {
      console.error('Multi-round batch scoring error:', error);
      setError(`Multi-round batch scoring failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    setMultiRoundInProgress(false);
  };

  return (
    <Box>
      {/* Search and Filter Section - Compact */}
      <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main', minWidth: 'max-content' }}>
            🔍 Filter:
        </Typography>
          
          <TextField
            label="Search by title"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 180, maxWidth: 200 }}
          />
          
          {/* Date Range - Compact */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Start Date"
              type="datetime-local"
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <Typography variant="caption" color="text.secondary">to</Typography>
            <TextField
              label="End Date"
              type="datetime-local"
              variant="outlined"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
          </Box>

          {/* Score Range - Compact */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 'max-content' }}>
              Score:
            </Typography>
            <TextField
              type="number"
              label="Low"
              value={lowScore}
              onChange={(e) => setLowScore(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
              size="small"
              inputProps={{ min: 0, max: 10 }}
              sx={{ width: 70 }}
            />
            <Typography variant="caption" color="text.secondary">-</Typography>
            <TextField
              type="number"
              label="High"
              value={highScore}
              onChange={(e) => setHighScore(Math.max(0, Math.min(10, parseInt(e.target.value) || 10)))}
              size="small"
              inputProps={{ min: 0, max: 10 }}
              sx={{ width: 70 }}
            />
          </Box>
          
          <Button 
            variant="contained" 
            onClick={handleFilter}
            size="small"
            sx={{ minWidth: 80 }}
          >
            FILTER
          </Button>
        </Box>
      </Paper>

      {/* Batch Scoring Section - Compact */}
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            '& > *': { flexShrink: 0 } // Prevent items from shrinking too much
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main', minWidth: 'max-content' }}>
              🤖 Batch Scoring:
            </Typography>
            <TextField
              type="number"
              label="Posts to Score"
              value={batchPostCount}
              onChange={(e) => setBatchPostCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
              size="small"
              inputProps={{ min: 1, max: 50 }}
              sx={{ width: 120 }}
              disabled={batchScoringInProgress || multiRoundInProgress}
            />
            <TextField
              type="number"
              label="Rounds (N)"
              value={batchRounds}
              onChange={(e) => setBatchRounds(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
              size="small"
              inputProps={{ min: 1, max: 1000 }}
              sx={{ width: 100 }}
              disabled={batchScoringInProgress || multiRoundInProgress}
            />
            <FormControl size="small" sx={{ minWidth: 180, maxWidth: 250 }}>
              <InputLabel>LLM Model</InputLabel>
              <Select
                value={batchScoringSelectedModel}
                onChange={(e) => setBatchScoringSelectedModel(e.target.value)}
                label="LLM Model"
                disabled={modelsLoading || batchScoringInProgress || multiRoundInProgress}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return <em>Select Model</em>;
                  const model = availableModels.find(m => m.id === selected);
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.2 }}>
                        {model?.name || selected}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {model?.provider} • ${model?.input_price || '0'}/1K
                      </Typography>
                    </Box>
                  );
                }}
                sx={{ 
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                {availableModels.map((model) => (
                  <MenuItem 
                    key={model.id} 
                    value={model.id}
                    title={`${model.name} (${model.provider}) - Input: $${model.input_price}/1K, Output: $${model.output_price}/1K`}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {model.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.provider} • In: ${model.input_price}/1K, Out: ${model.output_price}/1K
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={(batchScoringInProgress || multiRoundInProgress) ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleBatchScoring}
              disabled={batchScoringInProgress || multiRoundInProgress}
              color="primary"
              size="small"
            >
              {multiRoundInProgress ? `Round ${currentRound}/${totalRounds}` : 
               batchScoringInProgress ? 'Scoring...' : 'Go Batch'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetInProgress || batchScoringInProgress || multiRoundInProgress}
              size="small"
            >
              {resetInProgress ? 'Resetting...' : 'Reset All Scores'}
            </Button>
            {batchScoringInProgress && (
              <Typography variant="caption" color="text.secondary">
                Processing...
              </Typography>
            )}
          </Box>
          
          {/* Progress Bar for Multi-Round Scoring */}
          {multiRoundInProgress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Progress: Round {currentRound} of {totalRounds} 
                ({multiRoundResults.totalScored} posts scored)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(currentRound / totalRounds) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {Math.round((currentRound / totalRounds) * 100)}%
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Multi-Round Results Summary */}
      {!multiRoundInProgress && multiRoundResults.totalScored > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              📊 Multi-Round Batch Results ({batchRounds} rounds completed)
            </Typography>
            
            {/* Summary Stats */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Total Posts Scored: {multiRoundResults.totalScored}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average per Round: {Math.round(multiRoundResults.totalScored / batchRounds)}
              </Typography>
              {multiRoundResults.totalTimeElapsed && (
                <Typography variant="body2" color="text.secondary">
                  ⏱️ Time Elapsed: {multiRoundResults.totalTimeElapsed}
                </Typography>
              )}
              {multiRoundResults.startTime && multiRoundResults.endTime && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Started: {multiRoundResults.startTime.toLocaleTimeString()} → 
                  Finished: {multiRoundResults.endTime.toLocaleTimeString()}
                </Typography>
              )}
            </Box>

            {/* Score Distribution */}
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Score Distribution:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[0,1,2,3,4,5,6,7,8,9,10].map(score => (
                <Chip
                  key={score}
                  label={`${score}: ${multiRoundResults.scoreDistribution[score] || 0}`}
                  size="small"
                  variant={multiRoundResults.scoreDistribution[score] ? "filled" : "outlined"}
                  color={score >= 7 ? "success" : score >= 5 ? "warning" : "default"}
                />
              ))}
            </Box>

            {/* Round-by-Round Results */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1">Round-by-Round Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {multiRoundResults.roundResults.map((result, index) => (
                    <ListItem key={index}>
                                          <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word' 
                          }}
                        >
                          Round {result.round}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word' 
                          }}
                        >
                          {result.scored} posts scored
                        </Typography>
                      }
                    />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Single Round Batch Results */}
      {showBatchResults && batchScoringResults.length > 0 && !multiRoundResults.totalScored && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ✅ Batch Scoring Results ({batchScoringResults.length} posts scored)
            </Typography>
            <List>
              {batchScoringResults.map((result, index) => (
                <ListItem key={result.url_md5} divider={index < batchScoringResults.length - 1}>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word',
                          lineHeight: 1.4
                        }}
                      >
                        {result.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                          Score: {result.score}/10
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            lineHeight: 1.4,
                            mt: 0.5
                          }}
                        >
                          {result.reason}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Button 
              variant="outlined" 
              onClick={() => setShowBatchResults(false)}
              sx={{ mt: 2 }}
            >
              Hide Results
            </Button>
          </CardContent>
        </Card>
      )}

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Total Posts Count Display - Compact */}
      <Box sx={{ mb: 1.5, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          📊 Total Posts: {totalCount.toLocaleString()}
        </Typography>
        {totalCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              Page {page} of {totalPages} ({postsPerPage} per page)
          </Typography>
        )}
        </Box>
      </Box>
      
      <List>
        {posts.map((post, index) => {
          const rowNumber = (page - 1) * postsPerPage + index + 1;
          return (
            <React.Fragment key={post.url_md5}>
              <ListItem>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {/* Score this post button */}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<PsychologyIcon />}
                    onClick={(e) => handleScorePost(post, e)}
                    sx={{ 
                      mr: 2, 
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      padding: '4px 8px'
                    }}
                  >
                    Score
                  </Button>
                  
                  {/* Existing content */}
                  <ListItemButton onClick={() => handlePostClick(post.url_md5)} sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="body2" sx={{ minWidth: '40px', fontWeight: 'bold', color: 'primary.main', mr: 2 }}>
                        {rowNumber}.
                      </Typography>
                      
                      {/* Score display */}
                      {post.score !== undefined && post.score !== null && (
                        <Chip 
                          size="small" 
                          label={`Score: ${post.score}`}
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 2, minWidth: '80px' }}
                        />
                      )}
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                flex: 1,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.4
                              }}
                            >
                              {post.title}
                            </Typography>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={(e) => handleSummaryPost(post, e)}
                              title="Generate summary"
                              sx={{ ml: 1 }}
                            >
                              <SummarizeIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(post.url, '_blank');
                              }}
                              title="Open original Reddit post"
                              sx={{ ml: 1 }}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            Created: {new Date(post.created_time).toLocaleString()} | Inserted: {new Date(post.inserted_time).toLocaleString()}
                          </Typography>
                        }
                      />
                    </Box>
                  </ListItemButton>
                </Box>
              </ListItem>
              <Collapse in={selectedPost === post.url_md5} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <SyntaxHighlighter 
                    language="json" 
                    style={solarizedlight} 
                    customStyle={{ 
                      maxHeight: '400px', 
                      overflowY: 'auto',
                      fontSize: '12px',
                      lineHeight: '1.4',
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere'
                    }}
                    wrapLines={true}
                    wrapLongLines={true}
                    showLineNumbers={true}
                  >
                    {(() => {
                      try {
                        const jsonData = JSON.parse(post.json_data);
                        // Process text fields to handle \n characters properly
                        const processTextFields = (obj: any): any => {
                          if (typeof obj === 'string') {
                            // Convert escaped \n to actual line breaks for better readability
                            return obj.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
                          } else if (Array.isArray(obj)) {
                            return obj.map(processTextFields);
                          } else if (obj && typeof obj === 'object') {
                            const processed: any = {};
                            for (const key in obj) {
                              processed[key] = processTextFields(obj[key]);
                            }
                            return processed;
                          }
                          return obj;
                        };
                        
                        const processedData = processTextFields(jsonData);
                        return JSON.stringify(processedData, null, 2);
                      } catch (error) {
                        return post.json_data;
                      }
                    })()}
                  </SyntaxHighlighter>
                </Box>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={handlePageChange} />
      </Box>

      {/* Scoring Modal */}
      <Dialog
        open={scoringModalOpen}
        onClose={handleCloseScoringModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              <Typography variant="h6" component="span">
                Reddit Post Scoring
              </Typography>
              {!isRunningLLM && !scoringResult && (
                <Chip 
                  label="Ready for LLM" 
                  color="success" 
                  size="small" 
                  variant="outlined"
                />
              )}
              {isRunningLLM && (
                <Chip 
                  label="Running..." 
                  color="warning" 
                  size="small" 
                  variant="outlined"
                  icon={<CircularProgress size={12} />}
                />
              )}
              {scoringResult && (
                <Chip 
                  label={scoringResult.success ? "Complete" : "Error"} 
                  color={scoringResult.success ? "success" : "error"} 
                  size="small" 
                  variant="filled"
                />
              )}
            </Box>
            <IconButton onClick={handleCloseScoringModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {currentScoringPost && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Post: {currentScoringPost.title}
            </Typography>
          )}
          
          {/* Model Selection and Run Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Model</InputLabel>
              <Select
                value={selectedModel}
                label="Model"
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isRunningLLM || modelsLoading}
              >
                {availableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box>
                      <Typography variant="body2">{model.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.provider}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={isRunningLLM ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleRunLLM}
              disabled={isRunningLLM || !completePrompt}
              sx={{ minWidth: 120 }}
            >
              {isRunningLLM ? 'Running...' : 'Run Analysis'}
            </Button>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Prompt" />
              <Tab 
                label="Results" 
                disabled={!scoringResult && !isRunningLLM}
              />
            </Tabs>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ height: '100%' }}>
            {/* Prompt Tab */}
            {activeTab === 0 && (
              <SyntaxHighlighter 
                language="markdown" 
                style={solarizedlight}
                customStyle={{ 
                  height: '100%',
                  margin: 0,
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}
                showLineNumbers
              >
                {completePrompt}
              </SyntaxHighlighter>
            )}

            {/* Results Tab */}
            {activeTab === 1 && (
              <Box sx={{ height: '100%', p: 3 }}>
                {isRunningLLM && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Running Analysis...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This might take up to 2 minutes
                    </Typography>
                  </Box>
                )}

                {!isRunningLLM && scoringResult && (
                  <Box sx={{ height: '100%' }}>
                    {scoringResult.success ? (
                      <Box>
                        {/* Success Result */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                            ✅ Analysis Complete
                          </Typography>
                          
                          {/* Score saved indicator */}
                          {scoringResult.scoreSaved && scoringResult.savedScore !== undefined && (
                            <Typography variant="body2" color="success.main" sx={{ mb: 1, fontWeight: 'medium' }}>
                              💾 Score {scoringResult.savedScore} saved to database
                            </Typography>
                          )}
                          
                          {scoringResult.usage && (
                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                              <Chip 
                                size="small" 
                                label={`Model: ${scoringResult.usage.model_name}`} 
                                variant="outlined" 
                              />
                              <Chip 
                                size="small" 
                                label={`Tokens: ${scoringResult.usage.total_tokens}`} 
                                variant="outlined"
                              />
                              <Chip 
                                size="small" 
                                label={`Time: ${scoringResult.response_time}ms`} 
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>
                        
                        <Divider sx={{ mb: 3 }} />
                        
                        {/* LLM Response */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          LLM Analysis Results:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: '#fafafa', maxHeight: '60vh', overflow: 'auto' }}>
                          <SyntaxHighlighter 
                            language="markdown" 
                            style={solarizedlight}
                            customStyle={{ 
                              margin: 0,
                              fontSize: '13px',
                              lineHeight: '1.5',
                              backgroundColor: 'transparent'
                            }}
                          >
                            {scoringResult.content || 'No content received'}
                          </SyntaxHighlighter>
                        </Paper>
                      </Box>
                    ) : (
                      <Box>
                        {/* Error Result */}
                        <Alert severity="error" sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            ❌ Analysis Failed
                          </Typography>
                          <Typography variant="body2">
                            {scoringResult.error || 'Unknown error occurred'}
                          </Typography>
                          {scoringResult.response_time && (
                            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                              Response time: {scoringResult.response_time}ms
                            </Typography>
                          )}
                        </Alert>
                        
                        <Typography variant="body2" color="text.secondary">
                          💡 <strong>Common issues:</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
                          <li>Check if REACT_APP_OPENROUTER_API_KEY is set in your environment</li>
                          <li>Verify your OpenRouter account has sufficient credits</li>
                          <li>Try a different model from the dropdown</li>
                          <li>Check your internet connection</li>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!isRunningLLM && !scoringResult && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <SettingsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Results Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Click "Run Analysis" to start the LLM scoring process
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            {activeTab === 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  💡 This complete prompt is ready to be sent to your LLM service for post scoring analysis
                </Typography>
                <Button onClick={handleCloseScoringModal} variant="outlined">
                  Close
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    navigator.clipboard.writeText(completePrompt);
                    // You could add a toast notification here
                  }}
                >
                  Copy Prompt
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {scoringResult?.success 
                    ? '✅ Analysis completed successfully'
                    : scoringResult?.error 
                      ? '❌ Analysis failed - check configuration'
                      : '⏳ Ready to run analysis'
                  }
                </Typography>
                <Button onClick={handleCloseScoringModal} variant="outlined">
                  Close
                </Button>
                {scoringResult?.success && (
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      navigator.clipboard.writeText(scoringResult.content || '');
                      // You could add a toast notification here
                    }}
                  >
                    Copy Results
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  startIcon={<PlayArrowIcon />}
                  onClick={handleRunLLM}
                  disabled={isRunningLLM || !completePrompt}
                >
                  Run Again
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Summary Modal */}
      <Dialog 
        open={summaryModalOpen} 
        onClose={handleCloseSummaryModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh', maxHeight: '800px' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SummarizeIcon color="primary" />
              <Typography variant="h5">
                Reddit Post Summary
              </Typography>
              <Chip 
                label="Ready for LLM" 
                color="success" 
                size="small" 
                variant="outlined"
              />
            </Box>
            <IconButton onClick={handleCloseSummaryModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Post: <strong>{currentSummaryPost?.title}</strong>
          </Typography>

          {/* Model Selection and Run Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Model</InputLabel>
              <Select
                value={summarySelectedModel}
                label="Model"
                onChange={(e) => setSummarySelectedModel(e.target.value)}
                disabled={modelsLoading}
              >
                {availableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {model.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.provider}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              startIcon={isRunningSummaryLLM ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleRunSummaryLLM}
              disabled={isRunningSummaryLLM || !summaryPrompt}
              sx={{ minWidth: 120 }}
            >
              {isRunningSummaryLLM ? 'Running...' : 'Run Analysis'}
            </Button>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={summaryActiveTab} onChange={handleSummaryTabChange}>
              <Tab label="Prompt" />
              <Tab 
                label="Results" 
                disabled={!summaryResult && !isRunningSummaryLLM}
              />
            </Tabs>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ height: '100%' }}>
            {/* Prompt Tab */}
            {summaryActiveTab === 0 && (
              <SyntaxHighlighter 
                language="markdown" 
                style={solarizedlight}
                customStyle={{ 
                  height: '100%',
                  margin: 0,
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}
                showLineNumbers
              >
                {summaryPrompt}
              </SyntaxHighlighter>
            )}

            {/* Results Tab */}
            {summaryActiveTab === 1 && (
              <Box sx={{ height: '100%', p: 3 }}>
                {isRunningSummaryLLM && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Generating Summary...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This might take up to 2 minutes
                    </Typography>
                  </Box>
                )}

                {!isRunningSummaryLLM && summaryResult && (
                  <Box sx={{ height: '100%' }}>
                    {summaryResult.success ? (
                      <Box>
                        {/* Success Result */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                            ✅ Summary Complete
                          </Typography>
                          
                          {summaryResult.usage && (
                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                              <Chip 
                                size="small" 
                                label={`Model: ${summaryResult.usage.model_name}`} 
                                variant="outlined" 
                              />
                              <Chip 
                                size="small" 
                                label={`Tokens: ${summaryResult.usage.total_tokens}`} 
                                variant="outlined"
                              />
                              <Chip 
                                size="small" 
                                label={`Time: ${summaryResult.response_time}ms`} 
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>
                        
                        {/* Summary Content */}
                        <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                          <SyntaxHighlighter 
                            language="markdown" 
                            style={solarizedlight}
                            customStyle={{ 
                              backgroundColor: 'transparent',
                              padding: 0,
                              margin: 0,
                              fontSize: '14px',
                              lineHeight: '1.6'
                            }}
                          >
                            {summaryResult.content || 'No content available'}
                          </SyntaxHighlighter>
                        </Paper>
                      </Box>
                    ) : (
                      <Box>
                        {/* Error Result */}
                        <Alert severity="error" sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            ❌ Summary Failed
                          </Typography>
                          <Typography variant="body2">
                            {summaryResult.error || 'Unknown error occurred'}
                          </Typography>
                          {summaryResult.response_time && (
                            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                              Response time: {summaryResult.response_time}ms
                            </Typography>
                          )}
                        </Alert>
                        
                        <Typography variant="body2" color="text.secondary">
                          💡 <strong>Common issues:</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
                          <li>Check if REACT_APP_OPENROUTER_API_KEY is set in your environment</li>
                          <li>Verify your OpenRouter account has sufficient credits</li>
                          <li>Try a different model from the dropdown</li>
                          <li>Check your internet connection</li>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!isRunningSummaryLLM && !summaryResult && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <SummarizeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Summary Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Click "Run Analysis" to generate a comprehensive summary
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              {summaryResult?.usage && (
                <Typography variant="caption" color="text.secondary">
                  💡 This complete summary is ready for review and sharing
                </Typography>
              )}
            </Box>
            
            {summaryResult?.success && summaryResult?.content && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(summaryResult.content || '');
                  }}
                  size="small"
                >
                  Copy Summary
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCloseSummaryModal}
                  size="small"
                >
                  Close
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Reset All Scores Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => !resetInProgress && setResetConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" color="warning.main">
              ⚠️ Reset All Scores
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reset all post scores to unscored state?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will:
          </Typography>
          <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Reset all existing post scores to -1 (unscored)</li>
            <li>Set null/empty scores to -1 (unscored)</li>
            <li>Posts will be available for batch scoring again</li>
            <li>Cannot be undone</li>
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 'medium' }}>
            ⚠️ This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetConfirmOpen(false)}
            disabled={resetInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetAllScores}
            color="warning"
            variant="contained"
            disabled={resetInProgress}
            startIcon={resetInProgress ? <CircularProgress size={16} /> : undefined}
          >
            {resetInProgress ? 'Resetting...' : 'Reset All Scores'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Batch Prompt Review Modal */}
      <Dialog 
        open={promptReviewOpen} 
        onClose={handleCancelPromptReview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              🔍 Review Batch Scoring Prompt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentBatchData?.posts_count || 0} posts • Model: {availableModels.find(m => m.id === batchScoringSelectedModel)?.name || batchScoringSelectedModel}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and optionally modify the prompt before sending to the LLM. This prompt will analyze {currentBatchData?.posts_count || 0} posts in batch.
          </Typography>
          
          {/* LLM Model Selection */}
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>LLM Model</InputLabel>
              <Select
                value={batchScoringSelectedModel}
                onChange={(e) => setBatchScoringSelectedModel(e.target.value)}
                label="LLM Model"
                disabled={modelsLoading}
                sx={{ 
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                {availableModels.map((model) => (
                  <MenuItem 
                    key={model.id} 
                    value={model.id}
                    title={`${model.name} (${model.provider})`}
                  >
                    {model.name} ({model.provider})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <TextField
            multiline
            rows={22}
            variant="outlined"
            value={batchPromptToReview}
            onChange={(e) => setBatchPromptToReview(e.target.value)}
            sx={{ 
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start'
              },
              '& .MuiOutlinedInput-input': {
                height: '100% !important',
                overflow: 'auto !important',
                resize: 'none',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '12px',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }
            }}
            placeholder="Batch scoring prompt will appear here..."
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              💡 Tip: You can modify the prompt to adjust scoring criteria or add specific instructions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Characters: {batchPromptToReview.length.toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCancelPromptReview}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExecuteBatchScoring}
            variant="contained"
            color="primary"
            disabled={!batchPromptToReview.trim()}
            startIcon={batchScoringInProgress ? <CircularProgress size={16} /> : null}
          >
            {batchScoringInProgress ? 'Executing...' : 'Execute Batch Scoring'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SavedPostsList; 